import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * CHAT GROUP — a band of the store panel, not a section of its own.
 *
 * The Merchant Chat Groups section is gone: a group belongs to a store, so it
 * is made and managed where the store is, and the list of every group is the
 * store list filtered to "No chat group". The one channel that belongs to no
 * store — Product Updates — is pinned above that list instead.
 *
 * Creating a group ASKS who is in it (Alex, 2026-10-27). It used to seed
 * silently from a role list that skipped store admins entirely.
 */

const ICheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const initials = (name) => (name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('');

const ROLE_WORDS = {
  owner: 'Owner',
  store_admin: 'Store admin',
  sales_admin: 'Team member',
};

export function StoreChat({ store, onChanged, children }) {
  const storeId = store?.id;
  const [state, setState] = useState({ loading: true, candidates: [], memberIds: [], lockedId: null, channel: null });
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [archive, setArchive] = useState(false);

  const hasGroup = store?.chat && store.chat !== 'none';

  const load = useCallback(async () => {
    if (!storeId) return;
    setState((s) => ({ ...s, loading: true }));
    setError('');

    try {
      const { data } = await axios.get('/api/merchant-chat/members', {
        params: { store_id: storeId },
        metadata: { silent: true },
      });
      const ids = (data?.member_ids || []).map(Number);
      setState({
        loading: false,
        candidates: data?.candidates || [],
        memberIds: ids,
        lockedId: Number(data?.locked_user_id) || null,
        channel: data?.channel || null,
      });
      setPicked(ids);
    } catch (e) {
      // 404 simply means there is no channel yet — that is a state, not a
      // failure, and the band offers to create one.
      if (e?.response?.status === 404) {
        setState({ loading: false, candidates: [], memberIds: [], lockedId: null, channel: null });
      } else {
        setError(e?.response?.data?.message || 'Could not read the chat group.');
        setState((s) => ({ ...s, loading: false }));
      }
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  // With no channel the members endpoint has nothing to answer with, so the
  // picker falls back to the store's seated people.
  const [fallback, setFallback] = useState([]);
  useEffect(() => {
    if (hasGroup || !storeId) return;
    let live = true;
    axios.get(`/api/stores/${storeId}/members`)
      .then(({ data }) => {
        if (!live) return;
        const rows = (data?.members || data?.items || []).map((m) => ({
          id: m.user_id || m.id,
          name: m.name || [m.first_name, m.last_name].filter(Boolean).join(' '),
          role: m.role,
        }));
        setFallback(rows);
        setPicked(rows.map((r) => r.id));
      })
      .catch(() => { if (live) setFallback([]); });
    return () => { live = false; };
  }, [hasGroup, storeId]);

  const people = hasGroup && state.candidates.length ? state.candidates : fallback;
  const lockedId = state.lockedId;

  const toggle = (id) => {
    if (id === lockedId) return;
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const run = async (fn, done) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(done);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const create = () => run(
    () => axios.post('/api/roam-admin/channels', { store_id: storeId, user_ids: picked }),
    'The group was created.',
  );

  const save = () => run(
    () => axios.put('/api/merchant-chat/members', { store_id: storeId, user_ids: picked }),
    'Who’s in it was updated.',
  );

  const remove = () => {
    const words = archive
      ? `Archive "${state.channel?.name || 'this group'}" in Roam? This cannot be undone and the name can never be used again.`
      : `Hide the chat for ${store?.name}? History is kept and the Roam group is left alone.`;

    if (!window.confirm(words)) return;

    const id = state.channel?.id;
    if (!id) {
      setError('There is no group to remove.');
      return;
    }

    run(
      () => axios.delete(`/api/roam-admin/channels/${id}`, { data: { archive_roam: archive } }),
      archive ? 'Removed and archived in Roam.' : 'Hidden in the app. The Roam group is untouched.',
    );
  };

  const names = people
    .filter((p) => picked.includes(p.id))
    .map((p) => (p.name || '').split(' ')[0])
    .filter(Boolean);

  return (
    <div className="rc-sc__band">
      <div className="rc-sc__band-head">
        <h4>Chat group</h4>
        <span className="rc-sc__spacer" />
        {hasGroup && (
          <a className="rc-sc__btn rc-sc__btn--sm" href="/messages" style={{ textDecoration: 'none' }}>Open chat</a>
        )}
      </div>

      {error && <p className="rc-sc__error" role="alert">{error}</p>}
      {notice && <p className="rc-sc__hint" role="status">{notice}</p>}

      {state.loading ? (
        <p className="rc-sc__hint">Loading…</p>
      ) : hasGroup ? (
        <>
          <div className="rc-sc__chat">
            <span className="rc-sc__chat-body">
              <span className="rc-sc__chat-name">{state.channel?.name || `${store?.name} x Retail Circle`}</span>
              <span className="rc-sc__chat-who">
                {names.length ? `${names.join(', ')} · plus the Retail Circle team` : 'Everyone with a store role'}
              </span>
            </span>
            {store.chat === 'live' && <span className="rc-sc__chat-live"><ICheck /> Live</span>}
            {store.chat === 'hidden' && <span className="rc-sc__chat-off">Hidden</span>}
            {store.chat === 'error' && <span className="rc-sc__chat-bad">Needs attention</span>}
          </div>

          <details className="rc-sc__fold">
            <summary>
              Who’s in it
              <span className="rc-sc__fold-sub">{picked.length} of {people.length}</span>
            </summary>
            <div className="rc-sc__fold-body">
              <Picks people={people} picked={picked} lockedId={lockedId} onToggle={toggle} />
              <p className="rc-sc__hint">
                Unticking someone removes them from the group. It does not remove them from the store.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="rc-sc__btn rc-sc__btn--primary" disabled={busy} onClick={save}>
                  {busy ? 'Saving…' : 'Save who’s in it'}
                </button>
              </div>
            </div>
          </details>

          <details className="rc-sc__fold">
            <summary>Remove the group</summary>
            <div className="rc-sc__fold-body">
              <p className="rc-sc__note rc-sc__note--bad">
                <span>
                  <strong>Remove</strong> hides the chat in the app and keeps the history. The Roam group is
                  left alone unless you tick the box, which cannot be undone and burns the name for good.
                </span>
              </p>
              <label className="rc-sc__pick" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={archive} onChange={(e) => setArchive(e.target.checked)} />
                <span className="rc-sc__person-body">
                  <span className="rc-sc__person-name">Also archive it in Roam</span>
                  <span className="rc-sc__person-meta">Irreversible. Leave this off unless you mean it.</span>
                </span>
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="rc-sc__btn rc-sc__btn--danger" disabled={busy} onClick={remove}>Remove</button>
              </div>
            </div>
          </details>
        </>
      ) : (
        <>
          <p className="rc-sc__hint">
            No group yet. It will be called “{store?.name} x Retail Circle”.
          </p>

          {people.length === 0 ? (
            <p className="rc-sc__note">
              <span>Nobody is on this store yet — add someone above and the group can be created.</span>
            </p>
          ) : (
            <>
              <Picks people={people} picked={picked} lockedId={lockedId} onToggle={toggle} />
              <p className="rc-sc__hint">
                Every role can be picked. The Retail Circle team is always in the group. Nobody is
                emailed — it appears in their Messages.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span className="rc-sc__hint" style={{ flex: '1 1 200px' }}>
                  {picked.length} of {people.length} selected
                </span>
                <button type="button" className="rc-sc__btn rc-sc__btn--primary" disabled={busy || picked.length === 0} onClick={create}>
                  {busy ? 'Creating…' : 'Create the group'}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* The one channel that belongs to no store — but whose audience is
          decided HERE, per store. */}
      {children}
    </div>
  );
}

function Picks({ people, picked, lockedId, onToggle }) {
  return (
    <div className="rc-sc__picks">
      {people.map((p) => {
        const on = picked.includes(p.id);
        const locked = p.id === lockedId;

        return (
          <label key={p.id} className={`rc-sc__pick ${on ? 'rc-sc__pick--on' : ''}`}>
            <input type="checkbox" checked={on} disabled={locked} onChange={() => onToggle(p.id)} />
            <span className="rc-sc__person-avatar" aria-hidden="true">{initials(p.name)}</span>
            <span className="rc-sc__person-body">
              <span className="rc-sc__person-name">{p.name || `User ${p.id}`}</span>
              <span className="rc-sc__person-meta">
                {ROLE_WORDS[p.role] || p.role}
                {locked ? ' · always included' : ''}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

export default StoreChat;
